import { useState } from "react"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Progress } from "@/components/ui/progress"
import { TodayWorkSchedule } from "@/components/TodayWorkSchedule"
import { 
  Plus, 
  Clock, 
  AlertTriangle, 
  CheckCircle, 
  Users,
  FileText,
  TrendingUp
} from "lucide-react"

const Home = () => {
  const [selectedDepartment, setSelectedDepartment] = useState("all")

  // Mock data for today's overview
  const todayStats = {
    totalTasks: 24,
    completed: 8,
    inProgress: 12,
    pending: 4,
    urgent: 3
  }

  const departments = [
    { id: "business", name: "業務部", tasks: 6, completed: 2 },
    { id: "tech", name: "技術部", tasks: 8, completed: 3 },
    { id: "production", name: "生產部", tasks: 7, completed: 2 },
    { id: "quality", name: "品保部", tasks: 3, completed: 1 }
  ]

  const urgentTasks = [
    { id: 1, title: "客戶A緊急訂單確認", department: "業務部", deadline: "今天 17:00" },
    { id: 2, title: "產品B規格審查", department: "技術部", deadline: "今天 16:00" },
    { id: 3, title: "機台C維護檢查", department: "生產部", deadline: "明天 09:00" }
  ]

  const todayTasks = [
    { id: 1, title: "訂單編號 #2024001 - 規格確認", department: "技術部", status: "進行中", priority: "高" },
    { id: 2, title: "訂單編號 #2024002 - 生產排程", department: "生產部", status: "待處理", priority: "中" },
    { id: 3, title: "客戶D滿意度調查", department: "業務部", status: "已完成", priority: "低" },
    { id: 4, title: "品質檢驗報告", department: "品保部", status: "進行中", priority: "高" }
  ]

  return (
    <div className="container mx-auto py-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground">今日概覽</h1>
          <p className="text-muted-foreground">
            {new Date().toLocaleDateString('zh-TW', { 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric',
              weekday: 'long'
            })}
          </p>
        </div>
        <div className="flex gap-2">
          <Button>
            <Plus className="h-4 w-4 mr-2" />
            新訂單
          </Button>
          <Button variant="outline">
            <FileText className="h-4 w-4 mr-2" />
            臨時任務
          </Button>
        </div>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">總任務</p>
                <p className="text-2xl font-bold">{todayStats.totalTasks}</p>
              </div>
              <div className="h-12 w-12 bg-primary/10 rounded-lg flex items-center justify-center">
                <TrendingUp className="h-6 w-6 text-primary" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">已完成</p>
                <p className="text-2xl font-bold text-green-600">{todayStats.completed}</p>
              </div>
              <div className="h-12 w-12 bg-green-100 rounded-lg flex items-center justify-center">
                <CheckCircle className="h-6 w-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">進行中</p>
                <p className="text-2xl font-bold text-blue-600">{todayStats.inProgress}</p>
              </div>
              <div className="h-12 w-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <Clock className="h-6 w-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">緊急</p>
                <p className="text-2xl font-bold text-red-600">{todayStats.urgent}</p>
              </div>
              <div className="h-12 w-12 bg-red-100 rounded-lg flex items-center justify-center">
                <AlertTriangle className="h-6 w-6 text-red-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Department Status */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              部門狀態
            </CardTitle>
            <CardDescription>各部門今日工作進度</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {departments.map((dept) => (
              <div key={dept.id} className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">{dept.name}</span>
                  <span className="text-sm text-muted-foreground">
                    {dept.completed}/{dept.tasks}
                  </span>
                </div>
                <Progress value={(dept.completed / dept.tasks) * 100} className="h-2" />
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Urgent Tasks */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-red-500" />
              緊急事項
            </CardTitle>
            <CardDescription>需要立即處理的任務</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {urgentTasks.map((task) => (
              <div key={task.id} className="flex items-center justify-between p-3 bg-red-50 rounded-lg border border-red-200">
                <div className="flex-1">
                  <p className="text-sm font-medium">{task.title}</p>
                  <p className="text-xs text-muted-foreground">{task.department}</p>
                </div>
                <div className="text-right">
                  <Badge variant="destructive" className="text-xs">
                    <Clock className="h-3 w-3 mr-1" />
                    {task.deadline}
                  </Badge>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>

      {/* Today's Work Schedule */}
      <TodayWorkSchedule />
    </div>
  )
}

export default Home